import type {
  NewServiceDeclareRequest,
  NewServiceDeclareResponse,
  NewServiceStatusResponse,
} from '@rheora/enrollment-contract';
import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { getNewServiceStatus, postNewServiceDeclare } from './new-service.fetchers';

/**
 * TanStack hooks for E-SF20. Pages call THESE, never the fetchers.
 */

export const newServiceQueryKeys = {
  status: (ref: string) => ['shop', 'new-service', 'status', ref] as const,
};

/**
 * Declare a build with no meter yet.
 *
 * ── WHY IT DOES NOT INVALIDATE THE FUNNEL SNAPSHOT ─────────────────────────
 * Every other funnel-mutating POST invalidates `["shop","funnel","snapshot"]`,
 * because it changes the state of the enrollment in progress. This one does not:
 * a declaration creates a record that OUTLIVES the funnel session by months and
 * writes nothing into it. There is no candidate, no plan and no quote — that is
 * the whole situation. Invalidating would refetch a snapshot that cannot have
 * changed, and would imply a coupling that does not exist.
 */
export function useNewServiceDeclareMutation(): UseMutationResult<
  NewServiceDeclareResponse,
  Error,
  NewServiceDeclareRequest
> {
  return useMutation({ mutationFn: postNewServiceDeclare });
}

/**
 * Read a pending service point's status from its emailed ref.
 *
 * `enabled` on a non-empty ref so the hook can sit on a route whose param has not
 * resolved yet without firing a request that is guaranteed to 404.
 */
export function useNewServiceStatus(ref: string): UseQueryResult<NewServiceStatusResponse, Error> {
  return useQuery({
    queryKey: newServiceQueryKeys.status(ref),
    queryFn: () => getNewServiceStatus(ref),
    enabled: ref !== '',
  });
}
